import React, { useState, useCallback, useEffect, useRef } from 'react';
import { StyleSheet, Pressable, TextInput, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import SidebarListItemLayout from './SidebarListItemLayout';
import useResponsiveness from '@/hooks/useResponsiveness';
import PropTypes from 'prop-types';
import Icon from '@/components/Icon';

const createStyles = (colors, isXS) => StyleSheet.create({
  input: {
    fontSize: isXS ? 15 : 14,
    color: colors.text_color_primary,
    flex: 1,
    minWidth: 0,
    padding: 0,
    margin: 0,
    height: '100%',
    ...(Platform.OS === 'web' && { outlineStyle: 'none', border: 'none' }),
    borderWidth: 0,
    backgroundColor: colors.solid_color_secondary,
    borderRadius: 4,
    paddingHorizontal: 4,
  }
});

function SidebarListItemRename({
  initialValue,
  onRename,
  onCancel,
  autoFocus = true,
  maxLength = 50,
}) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const [value, setValue] = useState(initialValue);
  const [isEditing, setIsEditing] = useState(autoFocus);
  const inputRef = useRef(null);
  const styles = createStyles(colors, isXS);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleSubmit = useCallback(() => {
    const trimmedValue = value.trim();
    if (trimmedValue && trimmedValue !== initialValue) {
      onRename(trimmedValue);
    } else {
      onCancel();
    }
    setIsEditing(false);
  }, [value, initialValue, onRename, onCancel]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  }, [handleSubmit, onCancel]);

  const handleBlur = useCallback(() => {
    handleSubmit();
  }, [handleSubmit]);

  return (
    <SidebarListItemLayout
      indentation={-1}
      isRightContainerVisible={true}
      title={
        <TextInput
          ref={inputRef}
          value={value}
          onChangeText={setValue}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          style={[
            styles.input,
            isEditing && styles.inputActive
          ]}
          maxLength={maxLength}
          selectTextOnFocus
          autoCapitalize="none"
          autoCorrect={false}
          autoComplete="off"
          spellCheck={false}
        />
      }
      noEllipsis
      rightIcon={
        <Pressable onPress={handleSubmit}>
          <Icon
            name="check"
            size={isXS ? 18 : 14}
            color={colors.text_color_secondary}
          />
        </Pressable>
      }
    />
  );
}

SidebarListItemRename.propTypes = {
  initialValue: PropTypes.string.isRequired,
  onRename: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  autoFocus: PropTypes.bool,
  maxLength: PropTypes.number,
};

export default React.memo(SidebarListItemRename); 